import { useMemo } from 'react';
import type { Layer } from '@deck.gl/core';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { useAgentsData } from '../../data/useAgentsData';
import { useAggregationConfig } from '../useAggregationConfig';
import { useMapColorPalette } from '../useMapColorPalette';
import { useMapVisibility } from '../useMapVisibility';
import type { Agent, Trip } from '../../../types/domain';
import { alpha, type RgbColor } from '../../../utils/color';
import { useTripsData } from '../../data/useTripsData';

type HeatmapData = {
  lon: number;
  lat: number;
  weight: number;
};

type WithNonNullable<
  T extends Record<string, unknown>,
  K extends keyof T,
> = T & {
  [P in K]-?: NonNullable<T[P]>;
};

type UseHeatmapLayer = {
  heatmapLayer: Layer;
};

const useHeatmapLayer = (): UseHeatmapLayer => {
  const { aggregationConfig } = useAggregationConfig();
  const { isAggregationVisible } = useMapVisibility();
  const { agents } = useAgentsData();
  const { trips } = useTripsData();
  const { getRgbColors, mapColorOpacity } = useMapColorPalette();

  const data = useMemo((): HeatmapData[] => {
    if (aggregationConfig?.type !== 'heatmap') {
      return [];
    }

    switch (aggregationConfig.dimension) {
      case 'home':
        return agents
          .filter(
            (
              agent: Agent
            ): agent is WithNonNullable<Agent, 'home_lon' | 'home_lat'> => {
              return (
                typeof agent.home_lon === 'number' &&
                typeof agent.home_lat === 'number'
              );
            }
          )
          .map(
            (
              agent: WithNonNullable<Agent, 'home_lon' | 'home_lat'>
            ): HeatmapData => {
              return {
                lon: agent.home_lon,
                lat: agent.home_lat,
                weight: agent.weight,
              };
            }
          );
      case 'work':
        return agents
          .filter(
            (
              agent: Agent
            ): agent is WithNonNullable<Agent, 'work_lon' | 'work_lat'> => {
              return (
                typeof agent.work_lon === 'number' &&
                typeof agent.work_lat === 'number'
              );
            }
          )
          .map(
            (
              agent: WithNonNullable<Agent, 'work_lon' | 'work_lat'>
            ): HeatmapData => {
              return {
                lon: agent.work_lon,
                lat: agent.work_lat,
                weight: agent.weight,
              };
            }
          );
      case 'start':
        return trips.map((trip: Trip): HeatmapData => {
          return {
            lon: trip.start_lon,
            lat: trip.start_lat,
            weight: trip.weight,
          };
        });
      case 'end':
        return trips.map((trip: Trip): HeatmapData => {
          return {
            lon: trip.end_lon,
            lat: trip.end_lat,
            weight: trip.weight,
          };
        });
      default:
        return [];
    }
  }, [aggregationConfig, agents, trips]);

  const colors = useMemo((): RgbColor[] => {
    return getRgbColors(8).map((color: RgbColor): RgbColor => {
      return alpha(color, 1);
    });
  }, [getRgbColors]);

  const heatmapLayer = new HeatmapLayer<HeatmapData>({
    id: 'heatmap-layer',
    data: data,
    getPosition: (data: HeatmapData): [number, number] => {
      return [data.lon, data.lat];
    },
    getWeight: (data: HeatmapData): number => {
      return data.weight;
    },
    colorRange: colors,
    opacity: mapColorOpacity,
    threshold: 0.03,
    pickable: false,
    visible: isAggregationVisible && aggregationConfig?.type === 'heatmap',
  });

  return { heatmapLayer };
};

export { useHeatmapLayer };
